import React, { useCallback, useEffect, useRef } from 'react';
import { UIGameSettingsDialog } from './UIGameSettingsDialog.tsx';
import { useGameStore } from '../../store/GameStoreContext.ts';
import { useGameUserSettingsStorage } from '../../hooks/useGameUserSettingsStorage.ts';
import { useGameUserSettingsSetVolume, useGameUserSettingsVolumeHotkeys } from '../../hooks/useGameUserSettingsVolume.ts';
import { useHasOnlyOneHumanPlayer } from '../../store/selectors/useHasOnlyOneHumanPlayer.ts';
import { useRequestPause } from '../../hooks/usePause.ts';
export const UIGameSettingsDialogContainer = ({ hiddenSettingsTabs })=>{
    useGameUserSettingsStorage();
    useGameUserSettingsVolumeHotkeys();
    const isOpenSettings = useGameStore((state)=>state.gameUserSettings.isOpenSettings);
    const setIsOpenSettings = useGameStore((state)=>state.gameUserSettings.setIsOpenSettings);
    const gameInfo = useGameStore((state)=>state.gameSettings.gameInfo);
    const volume = useGameStore((state)=>state.gameUserSettings.volume);
    const isMuted = useGameStore((state)=>state.gameUserSettings.isMuted);
    const setVolume = useGameUserSettingsSetVolume();
    const isAnimationsEnabled = useGameStore((state)=>state.gameUserSettings.isAnimationsEnabled);
    const setIsAnimationsEnabled = useGameStore((state)=>state.gameUserSettings.setIsAnimationsEnabled);
    const isAntialiasEnabled = useGameStore((state)=>state.gameUserSettings.isAntialiasEnabled);
    const setIsAntialiasEnabled = useGameStore((state)=>state.gameUserSettings.setIsAntialiasEnabled);
    const resolution = useGameStore((state)=>state.gameUserSettings.resolution);
    const setResolution = useGameStore((state)=>state.gameUserSettings.setResolution);
    const powerPreference = useGameStore((state)=>state.gameUserSettings.powerPreference);
    const setPowerPreference = useGameStore((state)=>state.gameUserSettings.setPowerPreference);
    const setCanvasOptions = useGameStore((state)=>state.gameUserSettings.setCanvasOptions);
    const isKeyboardShortcutsEnabled = useGameStore((state)=>state.gameUserSettings.isKeyboardShortcutsEnabled);
    const setIsKeyboardShortcutsEnabled = useGameStore((state)=>state.gameUserSettings.setIsKeyboardShortcutsEnabled);
    const sendSettingsOpenedAnalytics = useGameStore((state)=>state.gameAnalytics.sendSettingsOpenedAnalytics);
    const sendSettingsClosedAnalytics = useGameStore((state)=>state.gameAnalytics.sendSettingsClosedAnalytics);
    const sendSettingsTabClickedAnalytics = useGameStore((state)=>state.gameAnalytics.sendSettingsTabClickedAnalytics);
    const sendSettingsValueChangedAnalytics = useGameStore((state)=>state.gameAnalytics.sendSettingsValueChangedAnalytics);
    const requestPause = useRequestPause();
    const hasOnlyOneHumanPlayer = useHasOnlyOneHumanPlayer();
    const debounceTimersRef = useRef(new Map());
    const sendSettingChanged = useCallback((key, value)=>{
        clearTimeout(debounceTimersRef.current.get(key));
        debounceTimersRef.current.set(key, setTimeout(()=>{
            debounceTimersRef.current.delete(key);
            sendSettingsValueChangedAnalytics(`${key}:${value}`);
        }, 1000));
    }, []);
    useEffect(()=>{
        if (isOpenSettings && hasOnlyOneHumanPlayer) requestPause();
    }, [
        isOpenSettings,
        hasOnlyOneHumanPlayer
    ]);
    useEffect(()=>{
        if (!isOpenSettings) return;
        sendSettingsOpenedAnalytics();
        return ()=>sendSettingsClosedAnalytics();
    }, [
        isOpenSettings
    ]);
    const handleVolumeChange = (value)=>{
        setVolume(value / 100);
        sendSettingChanged('volume', Math.round(value));
    };
    const handleAnimationsChange = (value)=>{
        setIsAnimationsEnabled(value);
        sendSettingChanged('animation', value);
    };
    const handleAntialiasChange = (value)=>{
        setCanvasOptions({
            antialias: value
        });
        setIsAntialiasEnabled(value);
        sendSettingChanged('antialias', value);
    };
    const handleResolutionChange = (value)=>{
        setCanvasOptions({
            resolution: value
        });
        setResolution(value);
        sendSettingChanged('resolution', value);
    };
    const handlePowerPreferenceChange = (value)=>{
        setCanvasOptions({
            powerPreference: value
        });
        setPowerPreference(value);
        sendSettingChanged('power_preference', value);
    };
    const handleKeyboardShortcutsChange = (value)=>{
        setIsKeyboardShortcutsEnabled(value);
        sendSettingChanged('keyboard_shortcuts', value);
    };
    return /*#__PURE__*/ React.createElement(UIGameSettingsDialog, {
        open: isOpenSettings,
        onClose: ()=>setIsOpenSettings(false),
        hiddenSettingsTabs: hiddenSettingsTabs,
        onTabChange: sendSettingsTabClickedAnalytics,
        audioSettings: {
            volume: volume * 100,
            isMuted: isMuted,
            onVolumeChanged: handleVolumeChange
        },
        displaySettings: {
            isAnimationsEnabled: isAnimationsEnabled,
            onAnimationsChange: handleAnimationsChange,
            isAntialiasEnabled: isAntialiasEnabled,
            onAntialiasChange: handleAntialiasChange,
            resolution: resolution,
            onResolutionChange: handleResolutionChange,
            powerPreference: powerPreference,
            onPowerPreferenceChange: handlePowerPreferenceChange
        },
        gameInfo: gameInfo,
        controlsSettings: {
            keyboardShortcutsEnabled: isKeyboardShortcutsEnabled,
            onKeyboardShortcutsChange: handleKeyboardShortcutsChange
        }
    });
};
